import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PERMISSION_META } from './config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeClass = (level) => {
  const m = PERMISSION_META[level];
  return m ? m.color : PERMISSION_META.none.color;
};

const RoleTemplatesTab = ({ staffRoles, defaultPermissions, moduleLabels, modules, onApplied }) => {
  const [applyingRole, setApplyingRole] = useState(null);

  const handleApplyToAll = async (role) => {
    if (!window.confirm(`Apply ${role} defaults to all users with this role? Their custom overrides will be cleared and active sessions invalidated.`)) return;
    setApplyingRole(role);
    try {
      const res = await axios.post(`${API}/permissions/roles/${encodeURIComponent(role)}/apply-to-all`);
      toast.success(res.data.message || `${role} defaults applied.`);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply role template');
    } finally {
      setApplyingRole(null);
    }
  };

  return (
    <Card data-testid="role-templates-card">
      <CardHeader>
        <CardTitle>Role Templates</CardTitle>
        <CardDescription>Default permissions per staff role. Apply to all users of that role in one click.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {staffRoles.map((role) => {
            const rolePerms = defaultPermissions[role] || {};
            const nonNoneModules = modules.filter((m) => (rolePerms[m] || 'none') !== 'none');
            return (
              <Card key={role} className="border" data-testid={`role-card-${role}`}>
                <CardHeader className="pb-2 flex-row items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-sm font-medium">{role}</CardTitle>
                    <CardDescription className="text-xs">
                      {nonNoneModules.length} of {modules.length} modules
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleApplyToAll(role)}
                    disabled={applyingRole === role}
                    data-testid={`apply-all-${role}`}
                  >
                    {applyingRole === role ? 'Applying...' : 'Apply to all'}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {nonNoneModules.length === 0 ? (
                      <p className="text-xs text-gray-400">No default access</p>
                    ) : (
                      nonNoneModules.map((m) => (
                        <div key={m} className="flex justify-between items-center text-xs">
                          <span className="text-gray-700 dark:text-gray-300 truncate mr-2">{moduleLabels[m]}</span>
                          <Badge className={`${badgeClass(rolePerms[m])} text-[10px] uppercase`}>
                            {rolePerms[m]}
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default RoleTemplatesTab;
